import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Share2, MessageCircle, ArrowLeft, ArrowRight } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { toast } from "sonner";
import { PROJECTS, CONTACT } from "../data/site";

const Gallery = ({ images, title }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: images.length > 1 });
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setIdx(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  return (
    <div className="relative" data-testid="project-gallery">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {images.map((src, i) => (
            <div key={src} className="min-w-0 flex-[0_0_100%] bg-[#F2F2F0]">
              <img src={src} alt={`${title} ${i + 1}`} loading={i === 0 ? "eager" : "lazy"} className="w-full h-[38vh] md:h-[45vh] object-contain" draggable="false" />
            </div>
          ))}
        </div>
      </div>

      {images.length > 1 && (
        <>
          <span data-testid="gallery-counter" className="absolute top-3 right-3 font-display text-xs font-bold bg-black/60 text-white px-3 py-1.5 rounded-full backdrop-blur-sm">
            {idx + 1} / {images.length}
          </span>
          <button
            onClick={scrollPrev}
            data-testid="gallery-prev-btn"
            aria-label="Previous image"
            className="hidden md:flex absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 backdrop-blur-sm items-center justify-center hover:bg-[#0047FF] hover:text-white transition-colors"
          >
            <ArrowLeft size={18} />
          </button>
          <button
            onClick={scrollNext}
            data-testid="gallery-next-btn"
            aria-label="Next image"
            className="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 backdrop-blur-sm items-center justify-center hover:bg-[#0047FF] hover:text-white transition-colors"
          >
            <ArrowRight size={18} />
          </button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => emblaApi && emblaApi.scrollTo(i)}
                data-testid={`gallery-dot-${i}`}
                aria-label={`Go to image ${i + 1}`}
                className={`h-2 rounded-full transition-all duration-300 ${i === idx ? "w-6 bg-[#0047FF]" : "w-2 bg-white/70"}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export const ProjectDrawer = ({ openIndex, setOpenIndex }) => {
  const open = openIndex !== null;
  const p = open ? PROJECTS[openIndex] : null;

  const next = () => setOpenIndex((openIndex + 1) % PROJECTS.length);
  const prev = () => setOpenIndex((openIndex - 1 + PROJECTS.length) % PROJECTS.length);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpenIndex(null);
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, openIndex]);

  const share = async () => {
    const url = `${window.location.origin}/#work`;
    const text = `${p.title}, a ${p.category} project by Arnav`;
    if (navigator.share) {
      try {
        await navigator.share({ title: text, url });
      } catch {}
    } else {
      await navigator.clipboard.writeText(`${text} ${url}`);
      toast.success("Link copied to clipboard");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="project-drawer-overlay"
          className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-end md:items-center justify-center"
          onClick={() => setOpenIndex(null)}
        >
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            data-testid="project-drawer"
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full md:max-w-4xl max-h-[92vh] md:max-h-[85vh] overflow-y-auto rounded-t-2xl md:rounded-lg relative"
          >
            <div className="sticky top-0 z-10 flex items-center justify-between px-5 md:px-8 py-4 bg-white/90 backdrop-blur-xl border-b border-black/5">
              <p className="font-display text-xs uppercase tracking-[0.25em] font-semibold text-[#0047FF]">
                {p.category} · {p.year}
              </p>
              <div className="flex items-center gap-2">
                <button onClick={share} data-testid="project-share-btn" aria-label="Share project" className="w-9 h-9 rounded-full border border-black/10 flex items-center justify-center hover:bg-[#FFB6C1]/40 transition-colors">
                  <Share2 size={16} />
                </button>
                <button onClick={() => setOpenIndex(null)} data-testid="project-close-btn" aria-label="Close" className="w-9 h-9 rounded-full border border-black/10 flex items-center justify-center hover:bg-black hover:text-white transition-colors">
                  <X size={16} />
                </button>
              </div>
            </div>

            <Gallery key={p.id} images={p.gallery || [p.image]} title={p.title} />

            <div className="px-5 md:px-8 py-6 md:py-8">
              <h3 className="font-display font-black tracking-tighter text-3xl md:text-5xl">{p.title}</h3>
              <p className="mt-3 text-black/60 leading-relaxed max-w-xl">{p.blurb}</p>
              <div className="mt-5 inline-block bg-[#0047FF] text-white font-display font-bold text-sm px-4 py-2 rounded-full">
                {p.result}
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href={`${CONTACT.whatsapp.split("?")[0]}?text=${encodeURIComponent(`Hi Arnav, I saw "${p.title}" on your portfolio. I want something like this.`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="project-contact-btn"
                  className="font-display font-bold px-6 py-3.5 rounded-full bg-[#0A0A0A] text-white hover:bg-[#0047FF] transition-colors flex items-center justify-center gap-2"
                >
                  <MessageCircle size={18} /> Message me about this
                </a>
                <div className="flex gap-3">
                  <button onClick={prev} data-testid="project-prev-btn" aria-label="Previous project" className="flex-1 sm:flex-none w-auto sm:w-12 h-12 rounded-full border border-black/10 flex items-center justify-center hover:border-[#0047FF] hover:text-[#0047FF] transition-colors">
                    <ChevronLeft size={20} />
                  </button>
                  <button onClick={next} data-testid="project-next-btn" aria-label="Next project" className="flex-1 sm:flex-none w-auto sm:w-12 h-12 rounded-full border border-black/10 flex items-center justify-center hover:border-[#0047FF] hover:text-[#0047FF] transition-colors">
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
              <p className="mt-4 text-xs text-black/30 md:hidden">Swipe the images · arrows switch projects</p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
